import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, request } from '../../api/client'

/**
 * Same account, same session mechanism as the customer application (design
 * §3) — `/api/auth/me` is the identical endpoint, just reached through this
 * app's own proxy. `isAdmin` is the one field the customer app's session
 * type has no use for and this one is built around.
 */
export interface SessionUser {
  userId: string
  companyId: string
  role: 'owner' | 'member'
  isAdmin: boolean
  firstName: string
  lastName: string
  email: string
  companyName: string
}

const SESSION_KEY = ['session'] as const

export function useSession() {
  return useQuery({
    queryKey: SESSION_KEY,
    retry: false,
    staleTime: 60_000,
    queryFn: async (): Promise<SessionUser | null> => {
      try {
        const { user } = await request<{ user: SessionUser }>('/api/auth/me')
        return user
      } catch (error) {
        // Not signed in is a normal state, not a failure to report.
        if (error instanceof ApiError && error.status === 401) return null
        throw error
      }
    },
  })
}

export function useLogin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      request<{ user: SessionUser }>('/api/auth/login', { method: 'POST', body }),
    onSuccess: ({ user }) => queryClient.setQueryData(SESSION_KEY, user),
  })
}

/**
 * `setQueryData(SESSION_KEY, null)` alone — not `queryClient.clear()` —
 * is deliberate: `clear()` drops the session query's cached value too,
 * which makes `useSession()` observe "no query" and refetch, flipping
 * `isPending` briefly true again. `AppRouter` unmounts everything below it
 * while `isPending`, including `LoginPage` — so a caller this hook logs
 * back out from (§3: a non-admin login, rejected in place, see
 * LoginPage.tsx) would lose the very message it just set, an instant
 * after showing it. `setQueryData` alone updates the cache synchronously
 * with no network round trip and no pending state, so `LoginPage` stays
 * mounted with its own `deniedAsNonAdmin` flag intact. A caller that
 * really does want every OTHER cached query gone on sign-out (the
 * "Abmelden" menu item, which navigates away regardless) clears the cache
 * itself — see router.tsx's `onLogout`.
 */
export function useLogout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
    onSuccess: () => {
      queryClient.setQueryData(SESSION_KEY, null)
    },
  })
}
